'use client'

import { FC, useLayoutEffect, useRef, useState } from 'react'
import type { ListingSeoIntroView } from '@shared/lib/seo/listing/buildListingSeoIntroView'
import { useAppSelector } from '@app/model/store/client'
import { selectListingState } from '@pages/Listing/model/selectors'
import { shouldOverrideListingSeoIntroWithRedux } from '@shared/lib/seo/listing/buildListingSeoIntroView'
import { IconButton } from '@shared/ui/Actions/IconButton'
import IconDots from '@icons/icon-dots.svg'
import discloseStyles from '@shared/ui/Actions/DiscloseText/DiscloseText.module.sass'
import { ListingDescriptionContent } from '@pages/Listing/ui/ListingDescriptionPage/ListingDescriptionContent'
import type { ReactNode } from 'react'

type Props = {
  serverIntro?: ReactNode
  initialIntroView: ListingSeoIntroView
}

const readCollapsedMaxHeight = (element: HTMLElement): number => {
  const parsed = parseFloat(getComputedStyle(element).getPropertyValue('--listing-description-collapsed-max-height'))
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * @description один ListingDescriptionContent. кнопка разворачивает тот же текст, второй h1 не монтируется
 */
const ListingDescriptionSlot: FC<Props> = ({ initialIntroView }) => {
  const reduxIntro = useAppSelector(selectListingState)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(true)
  const shouldOverride = shouldOverrideListingSeoIntroWithRedux(reduxIntro, initialIntroView)
  const title = shouldOverride ? (reduxIntro.title ?? initialIntroView.title) : initialIntroView.title

  const description = shouldOverride
    ? (reduxIntro.description ?? initialIntroView.description)
    : initialIntroView.description

  useLayoutEffect(() => {
    setExpanded(false)
    const element = contentRef.current
    if (!element) return

    const measure = () => {
      const collapsed = readCollapsedMaxHeight(element)
      const textHeight = element.querySelector('p')?.scrollHeight ?? 0
      setOverflows(textHeight > collapsed + 1)
    }

    measure()
    const text = element.querySelector('p')
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    if (text) observer.observe(text)
    return () => observer.disconnect()
  }, [description])

  const enhance =
    description && overflows && !expanded ? (
      <IconButton
        sizes='S'
        icon={IconDots}
        mode='white-border'
        data-baseline-enhance='listing.disclose'
        className={`${discloseStyles.button} ${discloseStyles.microButton}`}
        onClick={() => setExpanded(true)}
      />
    ) : null

  return (
    <ListingDescriptionContent
      title={title}
      description={description}
      expanded={expanded}
      clipped={overflows && !expanded}
      contentRef={contentRef}
      enhance={enhance}
    />
  )
}

export default ListingDescriptionSlot
